import * as THREE from 'three';

/**
 * ZombieModel - A low-poly zombie character model 
 * with glowing yellow eyes and simplified body structure.
 * Resource-efficient for large numbers of zombies.
 */
export class ZombieModel extends THREE.Group {
    constructor(options = {}) {
        super();
        
        // Configuration options with defaults
        this.config = {
            bodyColor: options.bodyColor || 0x454545, // Grayish/greenish dead skin color
            bodyEmissive: options.bodyEmissive || 0x001000, // Slight green glow
            eyeColor: options.eyeColor || 0xffff00, // Bright yellow eyes
            eyeEmissive: options.eyeEmissive || 0xffff00, // Yellow glow
            eyeIntensity: options.eyeIntensity || 0.8, // Brightness of eye glow
            clothesColor: options.clothesColor || 0x222222, // Dark clothes
            // Changed to 0.6 to be 0.8 of the typical player size of 0.7
            scale: options.scale || 0.56, 
            animationSpeed: options.animationSpeed || 0.7 // Slower, shambling animation
        };
        
        // Animation state
        this.animationState = {
            walking: true, // Zombies are always shambling
            attacking: false,
            attackCooldown: 0,
            time: 0
        };
        
        // Create body parts
        this.createBodyParts();
        
        // Set up structure
        this.assembleModel();
        
        // Apply initial scale
        this.scale.set(this.config.scale, this.config.scale, this.config.scale);
    }
    
    /**
     * Create all individual body part meshes
     */
    createBodyParts() {
        // Create materials
        this.bodyMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.bodyColor,
            roughness: 0.9, // Rough, dry skin
            metalness: 0.0,
            emissive: this.config.bodyEmissive,
            emissiveIntensity: 0.1
        });
        
        this.clothesMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.clothesColor,
            roughness: 0.8,
            metalness: 0.0
        });
        
        // Create glowing eye material
        this.eyeMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.eyeColor,
            emissive: this.config.eyeEmissive,
            emissiveIntensity: this.config.eyeIntensity,
            roughness: 0.2,
            metalness: 0.8
        });
        
        // Head with separate eyes
        this.head = new THREE.Mesh(
            new THREE.BoxGeometry(0.6, 0.6, 0.6),
            this.bodyMaterial
        );
        
        // Eyes (as separate meshes for glow effect)
        this.leftEye = new THREE.Mesh(
            new THREE.SphereGeometry(0.08, 6, 6), // Low poly sphere for eye
            this.eyeMaterial
        );
        
        this.rightEye = new THREE.Mesh(
            new THREE.SphereGeometry(0.08, 6, 6),
            this.eyeMaterial
        );
        
        // Torso
        this.torso = new THREE.Mesh(
            new THREE.BoxGeometry(0.8, 1.2, 0.4),
            this.clothesMaterial
        );
        
        // Arms
        this.upperRightArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.7, 0.25),
            this.bodyMaterial
        );
        
        this.lowerRightArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.6, 0.2),
            this.bodyMaterial
        );
        
        this.upperLeftArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.7, 0.25),
            this.bodyMaterial
        );
        
        this.lowerLeftArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.6, 0.2),
            this.bodyMaterial
        );
        
        // Legs
        this.upperRightLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.7, 0.25),
            this.clothesMaterial
        );
        
        this.lowerRightLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.6, 0.2),
            this.bodyMaterial
        );
        
        this.upperLeftLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.25, 0.7, 0.25),
            this.clothesMaterial
        );
        
        this.lowerLeftLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.6, 0.2),
            this.bodyMaterial
        );
    }
    
    /**
     * Assemble the model with proper hierarchy
     */
    assembleModel() {
        // Create joints (empty groups for rotation)
        this.neck = new THREE.Group();
        this.rightShoulder = new THREE.Group();
        this.leftShoulder = new THREE.Group();
        this.rightElbow = new THREE.Group();
        this.leftElbow = new THREE.Group();
        this.rightHip = new THREE.Group();
        this.leftHip = new THREE.Group();
        this.rightKnee = new THREE.Group();
        this.leftKnee = new THREE.Group();
        
        // Add parts to the model with proper hierarchy
        
        // Head and neck
        this.neck.position.set(0, 0.8, 0);
        this.neck.add(this.head);
        this.head.position.set(0, 0.35, 0);
        
        // Add eyes to head
        this.leftEye.position.set(0.15, 0.1, 0.31);
        this.rightEye.position.set(-0.15, 0.1, 0.31);
        this.head.add(this.leftEye);
        this.head.add(this.rightEye);
        
        // Add neck to torso
        this.torso.add(this.neck);
        
        // Right arm (from zombie's perspective)
        this.rightShoulder.position.set(-0.5, 0.6, 0);
        this.rightShoulder.add(this.upperRightArm);
        this.upperRightArm.position.set(0, -0.35, 0);
        this.torso.add(this.rightShoulder);
        
        this.rightElbow.position.set(0, -0.7, 0);
        this.rightElbow.add(this.lowerRightArm);
        this.lowerRightArm.position.set(0, -0.3, 0);
        this.upperRightArm.add(this.rightElbow);
        
        // Left arm
        this.leftShoulder.position.set(0.5, 0.6, 0);
        this.leftShoulder.add(this.upperLeftArm);
        this.upperLeftArm.position.set(0, -0.35, 0);
        this.torso.add(this.leftShoulder);
        
        this.leftElbow.position.set(0, -0.7, 0);
        this.leftElbow.add(this.lowerLeftArm);
        this.lowerLeftArm.position.set(0, -0.3, 0);
        this.upperLeftArm.add(this.leftElbow);
        
        // Right leg
        this.rightHip.position.set(-0.25, -0.6, 0);
        this.rightHip.add(this.upperRightLeg);
        this.upperRightLeg.position.set(0, -0.35, 0);
        this.torso.add(this.rightHip);
        
        this.rightKnee.position.set(0, -0.7, 0);
        this.rightKnee.add(this.lowerRightLeg);
        this.lowerRightLeg.position.set(0, -0.3, 0);
        this.upperRightLeg.add(this.rightKnee);
        
        // Left leg
        this.leftHip.position.set(0.25, -0.6, 0);
        this.leftHip.add(this.upperLeftLeg);
        this.upperLeftLeg.position.set(0, -0.35, 0);
        this.torso.add(this.leftHip);
        
        this.leftKnee.position.set(0, -0.7, 0);
        this.leftKnee.add(this.lowerLeftLeg);
        this.lowerLeftLeg.position.set(0, -0.3, 0);
        this.upperLeftLeg.add(this.leftKnee);
        
        // Add torso to the root of the model
        this.add(this.torso);
        
        // Center the model so the feet are at y=0
        this.torso.position.set(0, 1.85, 0);
        
        // Set initial zombie pose (slightly hunched, arms forward)
        this.setZombiePose();
    }
    
    /**
     * Set the default zombie pose - hunched and arms forward
     */
    setZombiePose() {
        // Hunched posture
        this.torso.rotation.x = 0.2;
        this.neck.rotation.x = -0.2;
        
        // Arms outstretched slightly
        this.rightShoulder.rotation.z = -0.3;
        this.leftShoulder.rotation.z = 0.3;
        this.rightShoulder.rotation.x = -0.4;
        this.leftShoulder.rotation.x = -0.4;
        
        // Bend elbows
        this.rightElbow.rotation.x = -0.5;
        this.leftElbow.rotation.x = -0.5;
        
        // Angle legs
        this.rightHip.rotation.x = -0.1;
        this.leftHip.rotation.x = -0.1;
    }
    
    /**
     * Update animations based on elapsed time
     * @param {number} deltaTime - Time since last frame in seconds
     */
    update(deltaTime) {
        // Accumulate animation time
        this.animationState.time += deltaTime * this.config.animationSpeed;
        
        // Handle attack cooldown
        if (this.animationState.attackCooldown > 0) {
            this.animationState.attackCooldown -= deltaTime;
            if (this.animationState.attackCooldown <= 0) {
                this.animationState.attacking = false;
            }
        }
        
        // Animate based on current state
        if (this.animationState.attacking) {
            this.animateZombieAttack(this.animationState.time);
        } else {
            this.animateZombieWalk(this.animationState.time);
        }
    }
    
    /**
     * Animate the zombie shambling walk
     */
    animateZombieWalk(time) {
        // Shambling walk cycle
        const armSwing = Math.sin(time * 2) * 0.15;
        const legSwing = Math.sin(time * 2) * 0.2;
        const bodyWobble = Math.sin(time * 1) * 0.06;
        
        // Torso wobble
        this.torso.rotation.z = bodyWobble;
        
        // Arm swinging
        this.rightShoulder.rotation.x = -0.4 + armSwing;
        this.leftShoulder.rotation.x = -0.4 - armSwing;
        
        // Leg movement
        this.rightHip.rotation.x = -0.1 + legSwing;
        this.leftHip.rotation.x = -0.1 - legSwing;
        
        // Head turning slightly
        this.neck.rotation.y = Math.sin(time * 0.5) * 0.1;
    }
    
    /**
     * Trigger the attack animation
     */
    startAttack() {
        // Only start attack if not already attacking
        if (!this.animationState.attacking) {
            this.animationState.attacking = true;
            this.animationState.attackCooldown = 1.0; // 1 second attack animation
            
            // Reset arm positions for attack animation
            this.rightShoulder.rotation.x = -0.8;
            this.leftShoulder.rotation.x = -0.8;
            this.rightElbow.rotation.x = -1.2;
            this.leftElbow.rotation.x = -1.2;
        }
    }
    
    /**
     * Animate the zombie attack
     */
    animateZombieAttack(time) {
        // Calculate attack phase (0 to 1)
        const phase = 1 - (this.animationState.attackCooldown / 1.0);
        
        // Attack consists of raising arms and then quickly bringing them down
        if (phase < 0.3) {
            // Wind up phase - raise arms
            const windUpPhase = phase / 0.3;
            this.rightShoulder.rotation.x = -0.8 - windUpPhase * 0.7;
            this.leftShoulder.rotation.x = -0.8 - windUpPhase * 0.7;
            this.rightElbow.rotation.x = -1.2 - windUpPhase * 0.3;
            this.leftElbow.rotation.x = -1.2 - windUpPhase * 0.3;
            
            // Also lean back slightly
            this.torso.rotation.x = 0.2 - windUpPhase * 0.2;
        } else {
            // Strike phase - bring arms down forcefully
            const strikePhase = (phase - 0.3) / 0.7;
            this.rightShoulder.rotation.x = -1.5 + strikePhase * 1.5;
            this.leftShoulder.rotation.x = -1.5 + strikePhase * 1.5;
            this.rightElbow.rotation.x = -1.5 + strikePhase * 0.5;
            this.leftElbow.rotation.x = -1.5 + strikePhase * 0.5;
            
            // Lunge forward
            this.torso.rotation.x = 0 + strikePhase * 0.3;
            
            // Also rotate head down with strike for extra menace
            this.neck.rotation.y = -0.2 - strikePhase * 0.3;
        }
    }
    
    /**
     * Helper method to make all meshes cast and receive shadows
     */
    enableShadows() {
        this.traverse(child => {
            if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });
    }
} 